import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Glyphicon } from 'react-bootstrap';
import Button from 'components/button/Button';
import TextField from 'components/forms/textField/TextField';
import Tooltip from 'components/tooltip/Tooltip';
import { LoadingSpinner } from 'components/loader/LoadingSpinner.styles';
import theme from 'styles/theme';
import useAppModal from 'hooks/useAppModal';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import {
  AgencyDnsSettingStatus,
  ApplicationDnsSetting,
  CustomDomainFormValues,
  WhitelabelSubjectApplication,
} from 'models/CustomDomains';
import useCreateAgencyDnsSetting from '../useCreateAgencyDnsSetting';
import useDeleteAgencyDnsSetting from '../useDeleteAgencyDnsSetting';
import { normalizeDomain } from '../customDomains.utils';
import customDomainSchema from './customDomainSchema';
import {
  CancelButton,
  FieldControl,
  FieldLabel,
  FieldRow,
  SectionActions,
  SectionForm,
  SectionHeader,
  StatusNote,
  StatusTag,
} from './CustomDomainModal.styles';

interface SectionBaseProps {
  application:
    | WhitelabelSubjectApplication.PLATFORM
    | WhitelabelSubjectApplication.DBS;
  dnsSetting?: ApplicationDnsSetting;
  onCheckConfiguration: () => void;
  onCancelSetting?: (setting: ApplicationDnsSetting) => void;
  isCheckingConfiguration: boolean;
  // migration mode: domain is read-only and the per-section Cancel/Save is hidden
  isMigration?: boolean;
}

interface CustomDomainSectionProps extends SectionBaseProps {
  onSettingSaved?: () => void;
}

const sectionCopy = {
  [WhitelabelSubjectApplication.PLATFORM]: {
    title: 'componentCustomDomains.sectionTitlePlatform',
    migrateTitle: 'componentCustomDomains.migrateSectionTitlePlatform',
    placeholder: 'componentCustomDomains.domainPlaceholderPlatform',
  },
  [WhitelabelSubjectApplication.DBS]: {
    title: 'componentCustomDomains.sectionTitleDbs',
    migrateTitle: 'componentCustomDomains.migrateSectionTitleDbs',
    placeholder: 'componentCustomDomains.domainPlaceholderDbs',
  },
} as const;

const statusLabels = {
  [AgencyDnsSettingStatus.PENDING]: 'componentCustomDomains.statusPending',
  [AgencyDnsSettingStatus.CONNECTED]: 'componentCustomDomains.statusConnected',
  [AgencyDnsSettingStatus.ERROR]: 'componentCustomDomains.statusError',
} as const;

// LEGACY excluded: no tenant to disable, and removal would break a legacy setup
const removableStatuses = [
  AgencyDnsSettingStatus.PENDING,
  AgencyDnsSettingStatus.CONNECTED,
  AgencyDnsSettingStatus.ERROR,
];

interface SectionContentProps extends SectionBaseProps {
  isCreating: boolean;
}

const SectionContent = ({
  application,
  dnsSetting,
  onCheckConfiguration,
  onCancelSetting,
  isCheckingConfiguration,
  isMigration,
  isCreating,
}: SectionContentProps) => {
  const { t } = useTranslation();
  const { getCopyAddonIcon } = useCopyToClipboard();
  const { watch } = useFormContext<CustomDomainFormValues>();
  const { deleteDnsSetting, isDeleting } = useDeleteAgencyDnsSetting();
  const { openConfirmModal } = useAppModal();

  const domainValue = watch('domain');
  const cnameTarget = dnsSetting?.cnameTarget ?? '';
  const status = dnsSetting?.status;
  const isPending = status === AgencyDnsSettingStatus.PENDING;
  // a saved section is locked: changing a domain is remove-then-add
  const isSaveDisabled = !domainValue?.trim() || !!dnsSetting || isCreating;
  // Cancel is purely the removal action, so it needs something removable
  const settingToRemove =
    dnsSetting?.domain && removableStatuses.includes(dnsSetting.status)
      ? dnsSetting
      : undefined;
  const { title, migrateTitle, placeholder } = sectionCopy[application];
  // never editable in place; an edit in migration mode provisions a new tenant
  const isDomainReadOnly = !!dnsSetting || isMigration;
  // LEGACY renders no tag; PENDING_DELETION is filtered out upstream
  const tagStatus =
    status &&
    status !== AgencyDnsSettingStatus.LEGACY &&
    status !== AgencyDnsSettingStatus.PENDING_DELETION
      ? status
      : undefined;

  const handleCancel = () => {
    if (!settingToRemove) {
      return;
    }

    const removeSetting = () =>
      deleteDnsSetting(settingToRemove, {
        onSuccess: () => onCancelSetting?.(settingToRemove),
      });

    // removing a live domain takes the customer's branded URL offline
    if (settingToRemove.status === AgencyDnsSettingStatus.CONNECTED) {
      openConfirmModal({
        title: t('componentCustomDomains.removeConfirmTitle'),
        body: t('componentCustomDomains.removeConfirmBody', {
          domain: settingToRemove.domain,
        }),
        confirmLabel: t('componentCustomDomains.removeConfirmLabel'),
        confirmColor: 'danger',
        onConfirm: removeSetting,
      });
      return;
    }

    removeSetting();
  };

  return (
    <>
      <SectionHeader>
        {t(isMigration ? migrateTitle : title)}
        {tagStatus && (
          <StatusTag
            $status={tagStatus}
            data-testid={`custom-domain-status-${application}`}
          >
            {t(statusLabels[tagStatus])}
          </StatusTag>
        )}
      </SectionHeader>

      <FieldRow>
        <FieldLabel htmlFor={`custom-domain-${application}`}>
          <Tooltip
            id={`custom-domain-tooltip-${application}`}
            text={t('componentCustomDomains.domainTooltip')}
          >
            <Glyphicon glyph="info-sign" />
          </Tooltip>
          {t('componentCustomDomains.cnameLabel')}
        </FieldLabel>
        <FieldControl>
          <TextField
            name="domain"
            id={`custom-domain-${application}`}
            label=""
            labelOnTop
            colSmInput={12}
            placeholder={t(placeholder)}
            readOnly={isDomainReadOnly}
            rightAddons={[getCopyAddonIcon(domainValue)]}
          />
        </FieldControl>
      </FieldRow>

      <FieldRow>
        <FieldLabel htmlFor={`custom-domain-cname-value-${application}`}>
          {t('componentCustomDomains.cnameValueLabel')}
        </FieldLabel>
        <FieldControl>
          <TextField
            name="cnameTarget"
            id={`custom-domain-cname-value-${application}`}
            label=""
            labelOnTop
            colSmInput={12}
            disabled
            rightAddons={[getCopyAddonIcon(cnameTarget)]}
          />
        </FieldControl>
      </FieldRow>

      {isPending && (
        <StatusNote data-testid={`custom-domain-pending-note-${application}`}>
          {t('componentCustomDomains.pendingNote')}{' '}
          <Button
            bsStyle="link"
            type="button"
            onClick={() => onCheckConfiguration()}
            disabled={isCheckingConfiguration}
            data-testid={`custom-domain-check-again-${application}`}
            leftIcon={
              isCheckingConfiguration ? (
                <LoadingSpinner $size="12px" $color={theme.colors.primary} />
              ) : null
            }
          >
            {t(
              isCheckingConfiguration
                ? 'componentCustomDomains.checkingAgain'
                : 'componentCustomDomains.checkAgain',
            )}
          </Button>
          {!isMigration && ` ${t('componentCustomDomains.pendingCancelHint')}`}
        </StatusNote>
      )}
      {status === AgencyDnsSettingStatus.ERROR && (
        <StatusNote
          $isError
          data-testid={`custom-domain-error-note-${application}`}
        >
          {t('componentCustomDomains.errorNote')}
          {!isMigration && ` ${t('componentCustomDomains.pendingCancelHint')}`}
        </StatusNote>
      )}

      {!isMigration && (
        <SectionActions>
          <CancelButton
            type="button"
            onClick={handleCancel}
            disabled={!settingToRemove || isCreating || isDeleting}
            data-testid={`custom-domain-cancel-${application}`}
          >
            {t('common.cancel')}
          </CancelButton>
          <Button type="submit" bsStyle="success" disabled={isSaveDisabled}>
            {t('componentCustomDomains.saveDomain')}
          </Button>
        </SectionActions>
      )}
    </>
  );
};

const CustomDomainSection = ({
  application,
  dnsSetting,
  onCheckConfiguration,
  onCancelSetting,
  onSettingSaved,
  isCheckingConfiguration,
  isMigration,
}: CustomDomainSectionProps) => {
  const { t } = useTranslation();
  const { createDnsSetting, isCreating } = useCreateAgencyDnsSetting();

  const handleSubmit = ({ domain }: CustomDomainFormValues) => {
    createDnsSetting(
      {
        domain: normalizeDomain(domain),
        application,
      },
      { onSuccess: onSettingSaved },
    );
  };

  return (
    <SectionForm
      key={`${dnsSetting?.domain ?? 'new'}_${dnsSetting?.cnameTarget ?? ''}`}
      schema={customDomainSchema(t)}
      defaultValues={{
        domain: dnsSetting?.domain ?? '',
        cnameTarget: dnsSetting?.cnameTarget ?? '',
      }}
      onSubmit={handleSubmit}
      data-testid={`custom-domain-section-${application}`}
    >
      <SectionContent
        application={application}
        dnsSetting={dnsSetting}
        onCheckConfiguration={onCheckConfiguration}
        onCancelSetting={onCancelSetting}
        isCheckingConfiguration={isCheckingConfiguration}
        isMigration={isMigration}
        isCreating={isCreating}
      />
    </SectionForm>
  );
};

CustomDomainSection.defaultProps = {
  dnsSetting: undefined,
  isMigration: false,
  onCancelSetting: undefined,
  onSettingSaved: undefined,
};

SectionContent.defaultProps = {
  dnsSetting: undefined,
  isMigration: false,
  onCancelSetting: undefined,
};

export default CustomDomainSection;
